import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ForemanArea } from '../../../types/ForemanArea';

const SERVICE_AREA_IDS_PARAM = 'serviceAreaIds';
const FOREMAN_AREA_IDS_PARAM = 'foremanAreaIds';

function dedupeAndSort(arr: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (let i = 0; i < arr.length; i++) {
    const s = arr[i];
    if (!seen.has(s)) {
      seen.add(s);
      out.push(s);
    }
  }
  return out.sort();
}

function parseIdList(param: string | null): number[] {
  if (param == null || param === '') return [];
  return param
    .split(',')
    .map((s) => parseInt(s.trim(), 10))
    .filter((n) => !isNaN(n));
}

function toParam(ids: number[]): string {
  return ids.filter((id) => id != null).join(',');
}

export interface ServiceAreaOption {
  id: number;
  name: string;
}

export interface ForemanAreaOption {
  id: number;
  name: string;
  serviceAreaId: number;
}

export interface UseHomeAreaFilterResult {
  /** Selected service area IDs (multiselect). Only areas the user has access to. */
  selectedServiceAreaIds: number[];
  /** Selected foreman area IDs (multiselect). Options depend on selected service areas. */
  selectedForemanAreaIds: number[];
  setServiceAreaIds: (ids: number[]) => void;
  setForemanAreaIds: (ids: number[]) => void;
  /** Set both area params in one navigation so the URL updates atomically (fixes Apply with only service area). */
  setAreaFilters: (serviceAreaIds: number[], foremanAreaIds: number[]) => void;
  /** Service area options: unique from user's foreman areas (access-based). */
  serviceAreaOptions: ServiceAreaOption[];
  /** Foreman area options: filtered by selected service area(s). */
  foremanAreaOptions: ForemanAreaOption[];
  /** Resolved service area name(s) for APIs that expect name. */
  serviceAreaNames: string[];
  /** Resolved foreman area names for task API (array). */
  foremanAreaNames: string[];
  /** First selected foreman area id for forms API (single). */
  foremanAreaIdForForms: string | undefined;
}

/**
 * Returns unique service areas the user has access to (from their foreman areas).
 */
export function getServiceAreaOptions(
  foremanAreas: ForemanArea[] | undefined
): ServiceAreaOption[] {
  if (!foremanAreas || foremanAreas.length === 0) return [];
  const byId = new Map<number, string>();
  for (const fa of foremanAreas) {
    const saId = fa.serviceAreaId;
    const name = fa.serviceArea?.name ?? `Service Area ${saId}`;
    if (!byId.has(saId)) byId.set(saId, name);
  }
  return Array.from(byId.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Returns foreman areas filtered by selected service area IDs.
 * When selectedServiceAreaIds is empty, returns all user foreman areas.
 */
export function getForemanAreaOptions(
  foremanAreas: ForemanArea[] | undefined,
  selectedServiceAreaIds: number[]
): ForemanAreaOption[] {
  if (!foremanAreas || foremanAreas.length === 0) return [];
  let list = foremanAreas;
  if (selectedServiceAreaIds.length > 0) {
    const set = new Set(selectedServiceAreaIds);
    list = foremanAreas.filter((fa) => set.has(fa.serviceAreaId));
  }
  return list
    .map((fa) => ({
      id: fa.id,
      name: fa.name,
      serviceAreaId: fa.serviceAreaId,
    }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Reads and writes homepage area filter state via URL search params.
 * Service Area and Foreman Area are both multiselect; foreman options cascade from service area selection.
 */
export function useHomeAreaFilter(foremanAreas: ForemanArea[] | undefined): UseHomeAreaFilterResult {
  const [searchParams, setSearchParams] = useSearchParams();

  const selectedServiceAreaIds = useMemo(
    () => parseIdList(searchParams.get(SERVICE_AREA_IDS_PARAM)),
    [searchParams]
  );

  const selectedForemanAreaIds = useMemo(
    () => parseIdList(searchParams.get(FOREMAN_AREA_IDS_PARAM)),
    [searchParams]
  );

  const serviceAreaOptions = useMemo(
    () => getServiceAreaOptions(foremanAreas),
    [foremanAreas]
  );

  const foremanAreaOptions = useMemo(
    () => getForemanAreaOptions(foremanAreas, selectedServiceAreaIds),
    [foremanAreas, selectedServiceAreaIds]
  );

  const setServiceAreaIds = useCallback(
    (ids: number[]) => {
      const next = new URLSearchParams(searchParams);
      next.set(SERVICE_AREA_IDS_PARAM, toParam(ids));
      const validForemanSet = new Set(
        getForemanAreaOptions(foremanAreas, ids).map((o) => o.id)
      );
      const keptForeman = selectedForemanAreaIds.filter((id) => validForemanSet.has(id));
      if (keptForeman.length !== selectedForemanAreaIds.length) {
        next.set(FOREMAN_AREA_IDS_PARAM, toParam(keptForeman));
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams, foremanAreas, selectedForemanAreaIds]
  );

  const setForemanAreaIds = useCallback(
    (ids: number[]) => {
      const next = new URLSearchParams(searchParams);
      next.set(FOREMAN_AREA_IDS_PARAM, toParam(ids));
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const setAreaFilters = useCallback(
    (serviceAreaIds: number[], foremanAreaIds: number[]) => {
      const next = new URLSearchParams(searchParams);
      next.set(SERVICE_AREA_IDS_PARAM, toParam(serviceAreaIds));
      const validForemanSet = new Set(
        getForemanAreaOptions(foremanAreas, serviceAreaIds).map((o) => o.id)
      );
      const keptForeman = foremanAreaIds.filter((id) => validForemanSet.has(id));
      next.set(FOREMAN_AREA_IDS_PARAM, toParam(keptForeman));
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams, foremanAreas]
  );

  const { serviceAreaNames, foremanAreaNames, foremanAreaIdForForms } = useMemo(() => {
    if (!foremanAreas?.length) {
      return {
        serviceAreaNames: [] as string[],
        foremanAreaNames: [] as string[],
        foremanAreaIdForForms: undefined as string | undefined,
      };
    }
    const saIdSet = new Set(selectedServiceAreaIds);
    const faIdSet = new Set(selectedForemanAreaIds);

    if (selectedForemanAreaIds.length > 0) {
      const names: string[] = [];
      const saNames: string[] = [];
      for (const fa of foremanAreas) {
        if (!faIdSet.has(fa.id)) continue;
        names.push(fa.name);
        if (fa.serviceArea?.name) saNames.push(fa.serviceArea.name);
      }
      const uniqueSaNames = dedupeAndSort(saNames);
      return {
        serviceAreaNames: uniqueSaNames,
        foremanAreaNames: names,
        foremanAreaIdForForms: String(selectedForemanAreaIds[0]),
      };
    }

    if (selectedServiceAreaIds.length > 0) {
      const names: string[] = [];
      for (const fa of foremanAreas) {
        if (!saIdSet.has(fa.serviceAreaId)) continue;
        names.push(fa.name);
      }
      // Derive service area names from options (same source as dropdown, with fallback) so filter always gets a valid name.
      // Use numeric comparison so string IDs from API match number IDs from URL.
      const saNamesFromOptions = selectedServiceAreaIds
        .map((id) => serviceAreaOptions.find((o) => Number(o.id) === Number(id))?.name)
        .filter((n): n is string => n != null);
      const uniqueSaNames = dedupeAndSort(saNamesFromOptions);
      return {
        serviceAreaNames: uniqueSaNames,
        foremanAreaNames: names,
        foremanAreaIdForForms: undefined,
      };
    }

    return {
      serviceAreaNames: [],
      foremanAreaNames: [],
      foremanAreaIdForForms: undefined,
    };
  }, [foremanAreas, selectedServiceAreaIds, selectedForemanAreaIds, serviceAreaOptions]);

  return {
    selectedServiceAreaIds,
    selectedForemanAreaIds,
    setServiceAreaIds,
    setForemanAreaIds,
    setAreaFilters,
    serviceAreaOptions,
    foremanAreaOptions,
    serviceAreaNames,
    foremanAreaNames,
    foremanAreaIdForForms,
  };
}
